import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Gavel, Plus, Loader2, Calendar, MapPin, Users, FileText, Download,
  CheckCircle2, XCircle, Lock, Trash2, ChevronRight, ChevronLeft, ListChecks
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('fr-FR'); } catch { return iso?.slice(0, 10) || '—'; } };

const STATUS_BADGE = {
  draft: { label: 'Brouillon', color: 'bg-amber-100 text-amber-700' },
  finalized: { label: 'Finalisee', color: 'bg-emerald-100 text-emerald-700' },
};

const RESOLUTION_BADGE = {
  approved: { label: 'Adoptee', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  rejected: { label: 'Rejetee', color: 'bg-red-100 text-red-700', icon: XCircle },
  pending: { label: 'En attente', color: 'bg-gray-100 text-gray-600', icon: ListChecks },
};

const GeneralAssemblies = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = ['admin', 'super_admin', 'agent'].includes(user?.role);

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [current, setCurrent] = useState(null);

  const [createForm, setCreateForm] = useState({
    type: 'ordinaire', date: '', location: '',
    agenda_text: '', president_id: '', secretary_id: '',
  });
  const [showResForm, setShowResForm] = useState(false);
  const [resForm, setResForm] = useState({ title: '', description: '' });

  useEffect(() => {
    if (!isAdmin) { toast.error("Reserve a l'administrateur"); navigate('/'); }
  }, [isAdmin, navigate]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const [a, u] = await Promise.all([api.get('/assemblies'), api.get('/users')]);
      setList(a.data || []);
      setUsers(u.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de chargement');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isAdmin) loadList(); }, [isAdmin, loadList]);

  const loadAssembly = useCallback(async (aid) => {
    if (!aid) { setCurrent(null); return; }
    try {
      const r = await api.get(`/assemblies/${aid}`);
      setCurrent(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  }, []);

  useEffect(() => { loadAssembly(currentId); }, [currentId, loadAssembly]);

  const handleCreate = async () => {
    if (!createForm.date) return toast.error('Date requise');
    try {
      const agenda = createForm.agenda_text
        .split('\n').map(s => s.trim()).filter(Boolean);
      const r = await api.post('/assemblies', { ...createForm, agenda });
      toast.success('Assemblee creee');
      setShowCreate(false);
      setCreateForm({ type: 'ordinaire', date: '', location: '', agenda_text: '', president_id: '', secretary_id: '' });
      await loadList();
      setCurrentId(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleTogglePresence = async (att) => {
    try {
      await api.put(`/assemblies/${current.id}/attendees`, {
        user_id: att.user_id,
        present: !att.present,
        represented_by_id: att.represented_by_id,
      });
      loadAssembly(current.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleAddResolution = async () => {
    if (!resForm.title.trim()) return toast.error('Titre requis');
    try {
      await api.post(`/assemblies/${current.id}/resolutions`, resForm);
      toast.success('Resolution ajoutee');
      setShowResForm(false);
      setResForm({ title: '', description: '' });
      loadAssembly(current.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleVote = async (rid, user_id, choice) => {
    try {
      await api.put(`/assemblies/${current.id}/resolutions/${rid}/vote`, { user_id, choice });
      loadAssembly(current.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleFinalize = async () => {
    if (!window.confirm("Finaliser l'assemblee ? Aucune modification ne sera plus possible.")) return;
    try {
      await api.post(`/assemblies/${current.id}/finalize`);
      toast.success('Assemblee finalisee');
      loadAssembly(current.id);
      loadList();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (asm) => {
    if (!window.confirm(`Supprimer l'AG du ${fmtDate(asm.date)} ?`)) return;
    try {
      await api.delete(`/assemblies/${asm.id}`);
      toast.success('AG supprimee');
      if (currentId === asm.id) setCurrentId(null);
      loadList();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDownloadPV = async (aid) => {
    try {
      toast.info('Generation du PV...');
      const r = await api.get(`/assemblies/${aid}/pv/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `PV_AG_${aid.slice(0, 8)}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Erreur PDF'); }
  };

  if (!isAdmin) return null;

  // ===== DETAIL VIEW =====
  if (current && currentId) {
    const isLocked = current.status === 'finalized';
    const presentCount = current.attendees.filter(a => a.present || a.represented_by_id).length;
    const presentShares = current.attendees
      .filter(a => a.present || a.represented_by_id)
      .reduce((s, a) => s + (a.shares || 0), 0);
    const quorum = current.total_shares_at_meeting > 0
      ? (presentShares / current.total_shares_at_meeting * 100).toFixed(2)
      : 0;

    return (
      <div className="space-y-6 animate-fade-in" data-testid="assembly-detail">
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="ghost" onClick={() => setCurrentId(null)} data-testid="back-to-list">
            <ChevronLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
          <h1 className="text-xl font-bold text-[#1B3A4B] flex items-center gap-2">
            <Gavel className="w-5 h-5 text-[#D4A745]" />
            AG {current.type} — {fmtDate(current.date)}
          </h1>
          <Badge className={STATUS_BADGE[current.status]?.color}>
            {isLocked && <Lock className="w-3 h-3 mr-1" />}{STATUS_BADGE[current.status]?.label}
          </Badge>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" onClick={() => handleDownloadPV(current.id)} data-testid="download-pv">
              <Download className="w-4 h-4 mr-2" /> PV PDF
            </Button>
            {!isLocked && (
              <Button onClick={handleFinalize} className="bg-emerald-600 hover:bg-emerald-700" data-testid="finalize-btn">
                <Lock className="w-4 h-4 mr-2" /> Finaliser
              </Button>
            )}
          </div>
        </div>

        {/* Meta */}
        <Card>
          <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <Meta icon={Calendar} label="Date" value={fmtDate(current.date)} />
            <Meta icon={MapPin} label="Lieu" value={current.location || '—'} />
            <Meta icon={Users} label="Quorum" value={`${presentShares} / ${current.total_shares_at_meeting} parts (${quorum}%)`} />
          </CardContent>
        </Card>

        {/* Agenda */}
        <Card>
          <CardContent className="p-4">
            <h2 className="font-semibold text-[#1B3A4B] mb-2">Ordre du jour</h2>
            {current.agenda?.length ? (
              <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1">
                {current.agenda.map((it, i) => <li key={i}>{it}</li>)}
              </ol>
            ) : <p className="text-gray-400 text-sm">Aucun point a l'ordre du jour</p>}
          </CardContent>
        </Card>

        {/* Attendees */}
        <Card>
          <CardContent className="p-0">
            <div className="p-4 border-b">
              <h2 className="font-semibold text-[#1B3A4B]">Feuille de presence ({presentCount}/{current.attendees.length})</h2>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-2">Actionnaire</th>
                  <th className="px-4 py-2">Parts</th>
                  <th className="px-4 py-2">Presence</th>
                </tr>
              </thead>
              <tbody>
                {current.attendees.map(a => (
                  <tr key={a.user_id} className="border-t" data-testid={`attendee-${a.user_id}`}>
                    <td className="px-4 py-2">{a.name}</td>
                    <td className="px-4 py-2 font-mono">{a.shares}</td>
                    <td className="px-4 py-2">
                      {isLocked ? (
                        <Badge className={a.present || a.represented_by_id ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}>
                          {a.present ? 'Present' : a.represented_by_id ? `Represente` : 'Absent'}
                        </Badge>
                      ) : (
                        <Button
                          size="sm" variant="outline"
                          onClick={() => handleTogglePresence(a)}
                          className={a.present ? 'border-emerald-300 text-emerald-700 bg-emerald-50' : ''}
                          data-testid={`toggle-presence-${a.user_id}`}
                        >
                          {a.present ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Present</> : 'Marquer present'}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Resolutions */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-[#1B3A4B]">Resolutions ({current.resolutions.length})</h2>
              {!isLocked && (
                <Button size="sm" onClick={() => setShowResForm(true)} className="bg-[#1B3A4B]" data-testid="add-resolution-btn">
                  <Plus className="w-4 h-4 mr-1" /> Ajouter
                </Button>
              )}
            </div>
            {current.resolutions.length === 0 && (
              <p className="text-gray-400 text-sm">Aucune resolution. Ajoutez-en avant de finaliser.</p>
            )}
            <div className="space-y-4">
              {current.resolutions.map((r, idx) => {
                const cfg = RESOLUTION_BADGE[r.status] || RESOLUTION_BADGE.pending;
                const Icon = cfg.icon;
                return (
                  <div key={r.id} className="border rounded-lg p-3" data-testid={`resolution-${r.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-[#1B3A4B]">Resolution {idx + 1} : {r.title}</p>
                        {r.description && <p className="text-xs text-gray-500 mt-0.5">{r.description}</p>}
                      </div>
                      <Badge className={cfg.color}><Icon className="w-3 h-3 mr-1" />{cfg.label}</Badge>
                    </div>

                    <div className="flex gap-2 mt-3 text-xs">
                      <span className="text-emerald-600">Pour : <b>{r.votes_for}</b></span>
                      <span className="text-red-600">Contre : <b>{r.votes_against}</b></span>
                      <span className="text-gray-500">Abst. : <b>{r.votes_abstain}</b></span>
                    </div>

                    {!isLocked && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs uppercase text-gray-400 mb-2">Saisir les votes (pondere par parts)</p>
                        <div className="space-y-2">
                          {current.attendees.map(a => {
                            const presentOrRep = a.present || a.represented_by_id;
                            const existing = r.votes?.find(v => v.user_id === a.user_id);
                            return (
                              <div key={a.user_id} className="flex items-center justify-between gap-2 text-sm">
                                <span className="flex-1 truncate">{a.name} <span className="text-gray-400">({a.shares} parts)</span></span>
                                <div className="flex gap-1">
                                  {['pour', 'contre', 'abstention'].map(choice => (
                                    <Button
                                      key={choice}
                                      size="sm"
                                      variant={existing?.choice === choice ? 'default' : 'outline'}
                                      className={existing?.choice === choice ? (choice === 'pour' ? 'bg-emerald-600' : choice === 'contre' ? 'bg-red-600' : 'bg-gray-500') : ''}
                                      disabled={!presentOrRep}
                                      onClick={() => handleVote(r.id, a.user_id, choice)}
                                      data-testid={`vote-${r.id}-${a.user_id}-${choice}`}
                                    >
                                      {choice}
                                    </Button>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* === Add resolution modal === */}
        <Dialog open={showResForm} onOpenChange={setShowResForm}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Nouvelle resolution</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Titre</Label>
                <Input value={resForm.title} onChange={e => setResForm(f => ({ ...f, title: e.target.value }))} data-testid="res-title" />
              </div>
              <div>
                <Label>Description (optionnel)</Label>
                <Textarea rows={3} value={resForm.description} onChange={e => setResForm(f => ({ ...f, description: e.target.value }))} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowResForm(false)}>Annuler</Button>
              <Button onClick={handleAddResolution} data-testid="res-save">Ajouter</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ===== LIST VIEW =====
  return (
    <div className="space-y-6 animate-fade-in" data-testid="assemblies-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1B3A4B] flex items-center gap-2">
            <Gavel className="w-6 h-6 text-[#D4A745]" />
            Assemblees Generales
          </h1>
          <p className="text-sm text-gray-500 mt-1">Convoquer, voter et generer les proces-verbaux</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="open-create-assembly">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle assemblee
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center"><Loader2 className="w-7 h-7 animate-spin text-[#1B3A4B] mx-auto" /></div>
          ) : list.length === 0 ? (
            <div className="p-12 text-center text-gray-400">
              <Gavel className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Aucune assemblee programmee</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Lieu</th>
                  <th className="px-4 py-3">Statut</th>
                  <th className="px-4 py-3">Resolutions</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map(asm => {
                  const sb = STATUS_BADGE[asm.status] || STATUS_BADGE.draft;
                  return (
                    <tr key={asm.id} className="border-t hover:bg-gray-50 cursor-pointer"
                        onClick={() => setCurrentId(asm.id)} data-testid={`assembly-row-${asm.id}`}>
                      <td className="px-4 py-3 font-medium">{fmtDate(asm.date)}</td>
                      <td className="px-4 py-3 capitalize">{asm.type}</td>
                      <td className="px-4 py-3 text-gray-600">{asm.location || '—'}</td>
                      <td className="px-4 py-3"><Badge className={sb.color}>{sb.label}</Badge></td>
                      <td className="px-4 py-3 text-center">{asm.resolutions_count}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-1" onClick={e => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" onClick={() => handleDownloadPV(asm.id)} title="PV PDF">
                            <FileText className="w-4 h-4 text-[#D4A745]" />
                          </Button>
                          {asm.status !== 'finalized' && (
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(asm)} title="Supprimer">
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => setCurrentId(asm.id)} title="Ouvrir">
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* === Create modal === */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nouvelle assemblee generale</DialogTitle>
            <DialogDescription>Un snapshot des actionnaires actuels sera realise.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={createForm.type} onValueChange={v => setCreateForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ordinaire">Ordinaire</SelectItem>
                    <SelectItem value="extraordinaire">Extraordinaire</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={createForm.date} onChange={e => setCreateForm(f => ({ ...f, date: e.target.value }))} data-testid="asm-date" />
              </div>
            </div>
            <div>
              <Label>Lieu</Label>
              <Input value={createForm.location} onChange={e => setCreateForm(f => ({ ...f, location: e.target.value }))} placeholder="Siege social, Bamako" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>President(e)</Label>
                <Select value={createForm.president_id} onValueChange={v => setCreateForm(f => ({ ...f, president_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Secretaire</Label>
                <Select value={createForm.secretary_id} onValueChange={v => setCreateForm(f => ({ ...f, secretary_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Ordre du jour (un point par ligne)</Label>
              <Textarea rows={4} value={createForm.agenda_text}
                        onChange={e => setCreateForm(f => ({ ...f, agenda_text: e.target.value }))}
                        placeholder={"Approbation des comptes\nAffectation du resultat\n..."} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button>
            <Button onClick={handleCreate} data-testid="asm-create-save">Creer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Meta = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2">
    <Icon className="w-4 h-4 text-[#D4A745] mt-0.5" />
    <div>
      <p className="text-[10px] uppercase tracking-wide text-gray-500">{label}</p>
      <p className="font-medium text-[#1B3A4B]">{value}</p>
    </div>
  </div>
);

export default GeneralAssemblies;
